import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Copy, ImageOff, MoreHorizontal, Play, RefreshCw, Send, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RelativeTime, StatusBadge, formatNumber } from '../../../components/design-system/admin';
import { MediaLightbox, LightboxMedia } from '../../../components/design-system/common';
import type { ContentPack, ContentPackItem } from '../../../common/hooks/use-content-packs-api.hook';
import { PACK_STATUS_TONE, captionWithTags, itemLabel, pushLines, splitItems } from './content-pack-view';

export type ItemAction = 'approve' | 'reject' | 'regenerate' | 'push';

interface PackCardProps {
    pack: ContentPack;
    highlighted?: boolean;
    /** Runs an action; resolves when the pack has been replaced. */
    onItemAction: (pack: ContentPack, key: string, action: ItemAction) => Promise<void>;
    onApproveAll: (pack: ContentPack) => Promise<void>;
}

const formatSlot = (iso: string) =>
    new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/**
 * One product's pack: what was made, what is still being made, and for each
 * piece the vendor's yes or no — then where it went.
 */
export const PackCard: React.FC<PackCardProps> = ({ pack, highlighted, onItemAction, onApproveAll }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState<string | null>(null);
    const [media, setMedia] = useState<LightboxMedia | null>(null);
    const { pictures, posts } = splitItems(pack);

    const run = async (key: string, action: ItemAction) => {
        setBusy(`${key}:${action}`);
        try {
            await onItemAction(pack, key, action);
        } finally {
            setBusy(null);
        }
    };
    const approveAll = async () => {
        setBusy('all');
        try {
            await onApproveAll(pack);
        } finally {
            setBusy(null);
        }
    };

    return (
        <article
            id={`pack-${pack.uuid}`}
            data-testid="content-pack"
            className={cn('rounded-xl border bg-card p-4 sm:p-5', highlighted && 'ring-[1.5px] ring-foreground')}
        >
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                    {pack.productThumbnailUrl ? (
                        <img src={pack.productThumbnailUrl} alt="" className="size-10 shrink-0 rounded-lg border object-cover" />
                    ) : (
                        <span className="size-10 shrink-0 rounded-lg border bg-muted" />
                    )}
                    <div className="min-w-0">
                        <h2 className="truncate text-sm font-medium">{pack.productTitle || pack.productUuid}</h2>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                            <span>{t(pack.trigger === 'auto' ? 'CONTENT_PACKS.PACK.AUTO' : 'CONTENT_PACKS.PACK.MANUAL')}</span>
                            <span aria-hidden>·</span>
                            <RelativeTime value={pack.createdAt} />
                            <span aria-hidden>·</span>
                            <span className="tabular-nums">{t('CONTENT_PACKS.PACK.CREDITS', { n: formatNumber(pack.creditsHeld || pack.creditsEstimated) })}</span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <StatusBadge tone={PACK_STATUS_TONE[pack.status]}>
                        {(pack.status === 'generating' || pack.status === 'queued') && <Spinner className="size-3" />}
                        {t(`CONTENT_PACKS.STATUS.${pack.status.toUpperCase()}`)}
                    </StatusBadge>
                    {pack.waiting > 0 && (
                        <Button size="sm" onClick={approveAll} disabled={busy !== null} data-testid="approve-all">
                            {busy === 'all' ? <Spinner data-icon="inline-start" /> : <Check data-icon="inline-start" />}
                            {t('CONTENT_PACKS.PACK.APPROVE_ALL', { n: pack.waiting })}
                        </Button>
                    )}
                </div>
            </header>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {pictures.map((item) => (
                    <PictureTile key={item.key} item={item} busy={busy} onRun={run} onOpen={setMedia} title={pack.productTitle} />
                ))}
            </div>

            {posts.length > 0 && (
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {posts.map((item) => (
                        <PostTile key={item.key} item={item} busy={busy} onRun={run} onOpen={setMedia} />
                    ))}
                </div>
            )}

            <MediaLightbox media={media} onClose={() => setMedia(null)} />
        </article>
    );
};

interface TileProps {
    item: ContentPackItem;
    busy: string | null;
    onRun: (key: string, action: ItemAction) => Promise<void>;
    onOpen: (m: LightboxMedia) => void;
    title?: string;
}

const PictureTile: React.FC<TileProps> = ({ item, busy, onRun, onOpen, title }) => {
    const { t } = useTranslation();
    const label = itemLabel(item);
    const ready = item.status === 'ready';
    const open = () => {
        if (item.videoUrl) onOpen({ url: item.videoUrl, kind: 'video', title });
        else if (item.imageUrl) onOpen({ url: item.imageUrl, kind: 'image', title });
    };
    return (
        <div className={cn('flex min-w-0 flex-col gap-2', item.approval === 'rejected' && 'opacity-60')} data-testid={`pack-item-${item.key}`}>
            <button
                type="button"
                onClick={open}
                disabled={!ready}
                aria-label={t(item.videoUrl ? 'CONTENT_PACKS.ITEM.PLAY' : 'CONTENT_PACKS.ITEM.OPEN')}
                className="relative aspect-[4/5] w-full overflow-hidden rounded-lg border bg-muted enabled:cursor-zoom-in"
            >
                {ready && item.imageUrl && <img src={item.imageUrl} alt="" loading="lazy" className="size-full object-cover" />}
                {ready && item.videoUrl && (
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex size-10 items-center justify-center rounded-full bg-background/90 shadow-sm">
                            <Play className="size-4 fill-current" />
                        </span>
                    </span>
                )}
                {(item.status === 'generating' || item.status === 'queued') && (
                    <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
                        <Spinner className="size-4" />
                        {t('CONTENT_PACKS.ITEM.MAKING')}
                    </span>
                )}
                {item.status === 'failed' && (
                    <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center text-xs text-muted-foreground">
                        <ImageOff className="size-4" />
                        {t('CONTENT_PACKS.ITEM.FAILED')}
                    </span>
                )}
                <ApprovalMark item={item} />
            </button>
            <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-medium">{t(`CONTENT_PACKS.${label.key}`, label.params)}</span>
                {!!item.creditsCost && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{t('CONTENT_PACKS.PACK.CREDITS', { n: item.creditsCost })}</span>}
            </div>
            <ItemControls item={item} busy={busy} onRun={onRun} />
        </div>
    );
};

const PostTile: React.FC<TileProps> = ({ item, busy, onRun, onOpen }) => {
    const { t } = useTranslation();
    const label = itemLabel(item);
    const media = item.imageUrl;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(captionWithTags(item));
            toast.success(t('CONTENT_PACKS.ITEM.COPIED'));
        } catch {
            /* clipboard refused: nothing to say */
        }
    };
    return (
        <div className={cn('flex min-w-0 flex-col gap-3 rounded-lg border p-3', item.approval === 'rejected' && 'opacity-60')} data-testid={`pack-item-${item.key}`}>
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    disabled={!media}
                    onClick={() => (item.videoUrl ? onOpen({ url: item.videoUrl, kind: 'video' }) : media && onOpen({ url: media, kind: 'image' }))}
                    className="relative aspect-[4/5] w-14 shrink-0 overflow-hidden rounded-md border bg-muted enabled:cursor-zoom-in"
                >
                    {media && <img src={media} alt="" loading="lazy" className="size-full object-cover" />}
                    {item.videoUrl && (
                        <span className="absolute inset-0 flex items-center justify-center">
                            <Play className="size-3.5 fill-background text-background" />
                        </span>
                    )}
                </button>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium">
                            {t(`CONTENT_PACKS.${label.key}`, label.params)}
                            {item.angle && <span className="font-normal text-muted-foreground"> · {item.angle}</span>}
                        </span>
                        {item.approval !== 'pending' && <ApprovalWord item={item} />}
                    </div>
                    {item.caption ? (
                        <p className="mt-1 line-clamp-4 text-xs leading-relaxed whitespace-pre-line text-foreground/90">{item.caption}</p>
                    ) : (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                            {item.status === 'failed' ? t('CONTENT_PACKS.ITEM.FAILED') : <><Spinner className="size-3" />{t('CONTENT_PACKS.ITEM.WRITING')}</>}
                        </p>
                    )}
                    {!!item.hashtags?.length && (
                        <p className="mt-1 line-clamp-1 text-xs text-muted-foreground" dir="auto">{item.hashtags.map((h) => `#${h}`).join(' ')}</p>
                    )}
                    {item.caption && item.status !== 'ready' && item.status !== 'failed' && (
                        <p className="mt-1 text-xs text-muted-foreground">{t('CONTENT_PACKS.ITEM.WAITING_PICTURE')}</p>
                    )}
                </div>
            </div>
            <ItemControls item={item} busy={busy} onRun={onRun} extra={item.caption ? { label: t('CONTENT_PACKS.ITEM.COPY_CAPTION'), icon: <Copy />, run: copy } : undefined} />
        </div>
    );
};

/** A small mark on a picture once decided. */
const ApprovalMark: React.FC<{ item: ContentPackItem }> = ({ item }) => {
    const { t } = useTranslation();
    if (item.approval === 'pending') return null;
    const yes = item.approval === 'approved';
    return (
        <span className="absolute start-2 top-2 inline-flex items-center gap-1 rounded-md bg-background/95 px-1.5 py-0.5 text-[11px] font-medium shadow-sm">
            {yes ? <Check className="size-3" /> : <X className="size-3" />}
            {t(yes ? 'CONTENT_PACKS.ITEM.APPROVED' : 'CONTENT_PACKS.ITEM.DECLINED')}
        </span>
    );
};

const ApprovalWord: React.FC<{ item: ContentPackItem }> = ({ item }) => {
    const { t } = useTranslation();
    const yes = item.approval === 'approved';
    return (
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
            {yes ? <Check className="size-3" /> : <X className="size-3" />}
            {t(yes ? 'CONTENT_PACKS.ITEM.APPROVED' : 'CONTENT_PACKS.ITEM.DECLINED')}
        </span>
    );
};

/**
 * Under each piece: approve / decline while it waits; where it went once
 * approved; make it again when it failed or was declined.
 */
const ItemControls: React.FC<{
    item: ContentPackItem;
    busy: string | null;
    onRun: (key: string, action: ItemAction) => Promise<void>;
    extra?: { label: string; icon: React.ReactNode; run: () => void };
}> = ({ item, busy, onRun, extra }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const working = (a: ItemAction) => busy === `${item.key}:${a}`;
    const disabled = busy !== null;
    const lines = pushLines(item, formatSlot);
    const canDecide = item.status === 'ready' && item.approval === 'pending';
    const canRemake = item.status === 'failed' || item.approval === 'rejected' || (item.status === 'ready' && item.approval === 'pending');

    return (
        <div className="flex flex-col gap-2">
            {lines.map((line) => (
                <div key={line.label} className="flex flex-col gap-1">
                    <StatusBadge tone={line.tone}>
                        {line.postUuid ? (
                            <button type="button" className="underline-offset-2 hover:underline" onClick={() => navigate(`/marketing/social-scheduler/post/${line.postUuid}`)}>
                                {t(`CONTENT_PACKS.PUSH.${line.label}`, line.labelParams)}
                            </button>
                        ) : (
                            t(`CONTENT_PACKS.PUSH.${line.label}`, line.labelParams)
                        )}
                    </StatusBadge>
                    {line.reason && <p className="text-xs text-muted-foreground">{t(`CONTENT_PACKS.PUSH.${line.reason}`, line.reasonParams)}</p>}
                    {(line.fix || line.retry) && (
                        <div className="flex flex-wrap gap-1.5">
                            {line.fix && (
                                <Button variant="outline" size="xs" onClick={() => navigate(line.fix!.route)}>
                                    {t(`CONTENT_PACKS.PUSH.${line.fix.label}`)}
                                </Button>
                            )}
                            {line.retry && (
                                <Button variant="outline" size="xs" onClick={() => onRun(item.key, 'push')} disabled={disabled}>
                                    {working('push') ? <Spinner data-icon="inline-start" /> : <Send data-icon="inline-start" />}
                                    {t('CONTENT_PACKS.ITEM.SEND_AGAIN')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            ))}

            {(canDecide || canRemake || extra) && (
                <div className="flex items-center gap-1.5">
                    {canDecide && (
                        <>
                            <Button variant="outline" size="sm" onClick={() => onRun(item.key, 'approve')} disabled={disabled} data-testid="approve">
                                {working('approve') ? <Spinner data-icon="inline-start" /> : <Check data-icon="inline-start" />}
                                {t('CONTENT_PACKS.ITEM.APPROVE')}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => onRun(item.key, 'reject')} disabled={disabled}>
                                {working('reject') ? <Spinner data-icon="inline-start" /> : <X data-icon="inline-start" />}
                                {t('CONTENT_PACKS.ITEM.DECLINE')}
                            </Button>
                        </>
                    )}
                    {!canDecide && canRemake && (
                        <Button variant="outline" size="sm" onClick={() => onRun(item.key, 'regenerate')} disabled={disabled}>
                            {working('regenerate') ? <Spinner data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                            {t('CONTENT_PACKS.ITEM.MAKE_AGAIN')}
                        </Button>
                    )}
                    {(canDecide || extra) && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon-sm" className="ms-auto" aria-label={t('CONTENT_PACKS.ITEM.MORE')} disabled={disabled}>
                                    <MoreHorizontal />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {extra && (
                                    <DropdownMenuItem onClick={extra.run}>
                                        {extra.icon}
                                        {extra.label}
                                    </DropdownMenuItem>
                                )}
                                {canDecide && (
                                    <DropdownMenuItem onClick={() => onRun(item.key, 'regenerate')}>
                                        <RefreshCw />
                                        {t('CONTENT_PACKS.ITEM.MAKE_AGAIN')}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            )}
        </div>
    );
};
